import { useCallback, useEffect, useState } from "react";
import { EyeOff, RotateCcw } from "lucide-react";
import { api, errorText } from "../lib/api";
import { CHECK_SHORT } from "../lib/hygiene";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUS = {
  applied: { text: "Hidden", cls: "bg-slate-100 text-slate-600", hint: "The finding exists and is hidden from the counts" },
  changed: { text: "Changed – shown again", cls: "bg-amber-100 text-amber-800", hint: "The finding changed since it was ignored (other group members / other detail), so it counts again" },
  gone: { text: "Fixed / gone", cls: "bg-live/10 text-live", hint: "The finding no longer exists – you can remove this entry" },
};

// Admin: findings marked as intentional (false positives) with their live status and a restore button
export const HygieneIgnores = ({ refreshKey }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/hygiene/ignores");
      setItems(r.data.ignores);
      setError("");
    } catch (e) {
      setError(errorText(e));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  const restore = async (ig) => {
    try {
      await api.delete(`/admin/hygiene/ignores/${ig.id}`);
      load();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <div className="space-y-2" data-testid="hygiene-ignores-section">
      <div className="eyebrow flex items-center gap-2"><EyeOff className="w-3.5 h-3.5" /> Ignored findings · {items?.length ?? "…"}</div>
      <p className="text-xs text-slate-500 max-w-3xl" data-testid="hygiene-ignores-help">
        Marked as intentional from the board's hygiene panel (admin only, with a check filter active: <i>Ignore</i> on a row, or <i>Ignore group</i> on a duplicate group). Hidden from every count while unchanged; if the group members or the detail change, the finding is shown again. <i>Restore</i> brings it back immediately.
      </p>
      {error && <div className="panel p-3 text-sm text-brand" data-testid="hygiene-ignores-error">{error}</div>}
      <div className="panel divide-y divide-panel-border" data-testid="hygiene-ignores-list">
        {items && !items.length && <div className="p-4 text-sm text-slate-500" data-testid="hygiene-ignores-empty">Nothing ignored – every finding counts.</div>}
        {(items || []).map((ig) => {
          const st = STATUS[ig.status] || STATUS.gone;
          return (
            <div key={ig.id} data-testid={`hygiene-ignore-row-${ig.id}`} className="px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="num shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">{ig.scope === "group" ? "group" : ig.type}</span>
              <span className="flex-1 min-w-[200px]">
                <span className="font-semibold text-ink">{ig.name}</span>
                <span className="block text-xs text-slate-500">
                  {CHECK_SHORT[ig.check] || ig.check}{ig.reason ? ` · “${ig.reason}”` : ""} · {when(ig.created_at)}
                </span>
              </span>
              <span data-testid={`hygiene-ignore-status-${ig.id}`} title={st.hint} className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.text}</span>
              <button type="button" data-testid={`hygiene-ignore-restore-${ig.id}`} onClick={() => restore(ig)} className="inline-flex items-center gap-1 rounded-full border border-panel-border px-3 py-1 text-xs text-slate-600 hover:border-slate-400 hover:text-ink transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> {ig.status === "gone" ? "Remove" : "Restore"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
